<template>
  <!-- was <body> — nested body breaks the DOM (two <body> elements) -->
  <div :data-sidebar="darkMode ? 'dark' : 'light'">
  <title>FLOCKR | My Orders</title>
  <div class="orders-root" :style="themePageStyle">
    <div class="ord-topbar">
      <div class="container d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center gap-3">
          <back-button class="me-2" />
          <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" /></a>
          <span style="color:var(--fm-h,#F5F0E8);font-weight:600;">My Orders</span>
        </div>
        <div class="d-flex gap-2">
          <a href="javascript:void(0)" @click="$router.push('/wallet')" class="btn btn-sm btn-outline-warning"><i class="mdi mdi-wallet me-1"></i>Wallet</a>
          <a href="javascript:void(0)" @click="$router.push('/disputes')" class="btn btn-sm btn-outline-danger"><i class="mdi mdi-shield-alert me-1"></i>Disputes</a>
          <a href="javascript:void(0)" @click="$router.push('/reviews')" class="btn btn-sm btn-outline-warning"><i class="mdi mdi-star me-1"></i>Reviews</a>
          <a href="javascript:void(0)" @click="$router.push('/escrow')" class="btn btn-sm btn-outline-secondary">Escrow</a>
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="btn btn-sm btn-outline-secondary">Dashboard</a>
        </div>
      </div>
    </div>
    <div class="container ord-container">
      <div class="ord-role-toggle mb-3">
        <button :class="{ active: role === 'buyer' }" @click="role='buyer'; fetchOrders()"><i class="mdi mdi-cart me-1"></i>Buying</button>
        <button :class="{ active: role === 'seller' }" @click="role='seller'; fetchOrders()"><i class="mdi mdi-tag me-1"></i>Selling</button>
      </div>
      <div class="ord-tabs mb-4">
        <button v-for="tab in statusTabs" :key="tab.value" :class="{ active: activeTab === tab.value }" @click="activeTab = tab.value">{{ tab.label }}
          <span v-if="getCount(tab.value)" class="ord-tab-badge">{{ getCount(tab.value) }}</span>
        </button>
      </div>
      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
      <div v-else-if="filteredOrders.length === 0" class="text-center py-5">
        <i class="mdi mdi-package-variant" style="font-size:48px;color:var(--fm-s,#2A2520);"></i>
        <p class="mt-2" style="color:var(--fm-s,#6B6356);">No orders found</p>
      </div>
      <div v-else>
        <div v-for="order in filteredOrders" :key="order.id" class="ord-card" @click="$router.push('/orders/' + order.id)">
          <div class="d-flex align-items-start gap-3">
            <div class="ord-card-img">
              <img v-if="order.listing_photo" :src="photoUrl(order.listing_photo)" />
              <i v-else class="mdi mdi-paw" style="font-size:24px;color:#BFA164;"></i>
            </div>
            <div class="flex-fill">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <span class="ord-order-num">#{{ order.order_number }}</span>
                <span class="ord-status-badge" :class="'st-' + order.status">{{ formatStatus(order.status) }}</span>
              </div>
              <div class="ord-card-title">{{ order.listing_title || 'Order' }}</div>
              <div class="ord-card-meta">
                <span v-if="role === 'buyer'">Seller: {{ order.seller_name }}</span>
                <span v-else>Buyer: {{ order.buyer_name }}</span>
                <span class="ms-3">{{ formatDate(order.created_at) }}</span>
              </div>
            </div>
            <div class="text-end">
              <div class="ord-card-price">{{ (order.currency || 'KES') }} {{ formatPrice(order.total_amount) }}</div>
            </div>
          </div>
          <div v-if="order.status === 'pending_payment' && role === 'buyer'" class="ord-hint warn"><i class="mdi mdi-alert me-1"></i>Payment pending</div>
          <div v-if="order.status === 'delivered' && role === 'buyer'" class="ord-hint ok"><i class="mdi mdi-check-circle me-1"></i>Confirm receipt to release payment</div>
          <div v-if="order.status === 'paid' && role === 'seller'" class="ord-hint info"><i class="mdi mdi-information me-1"></i>Paid - confirm and prepare delivery</div>
          <div v-if="order.status === 'payment_initiated' && role === 'buyer'" class="ord-hint warn"><i class="mdi mdi-cellphone me-1"></i>Check your phone for M-Pesa prompt</div>
          <div v-if="order.status === 'in_transit'" class="ord-hint info"><i class="mdi mdi-truck me-1"></i>Livestock is on the way</div>
          <div v-if="order.status === 'pending_seller_confirmation' && role === 'seller'" class="ord-hint warn"><i class="mdi mdi-truck-check me-1"></i>Transporter picked up - confirm the handover</div>
          <div v-if="order.status === 'pending_seller_confirmation' && role === 'buyer'" class="ord-hint info"><i class="mdi mdi-truck-fast me-1"></i>Picked up - awaiting seller confirmation</div>
          <div v-if="order.status === 'inspection' && role === 'buyer'" class="ord-hint ok"><i class="mdi mdi-eye me-1"></i>Inspect and approve to release payment</div>
          <div v-if="order.status === 'disputed'" class="ord-hint warn" style="cursor:pointer;" @click.stop="$router.push('/disputes')"><i class="mdi mdi-gavel me-1"></i>Under dispute resolution<i class="mdi mdi-chevron-right ms-1"></i></div>

          <!-- Partially-paid: split payment in progress. Show progress + "Send next prompt" CTA. -->
          <div v-if="order.status === 'partially_paid' && role === 'buyer'" class="ord-hint partial" style="background:rgba(191,161,100,.08);border:1px solid rgba(191,161,100,.3);color:var(--fm-h,#F5F0E8);">
            <div style="display:flex;justify-content:space-between;font-size:11px;color:#BFA164;font-weight:600;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px;">
              <span><i class="mdi mdi-call-split me-1"></i>Split payment in progress</span>
              <span>{{ paidPct(order) }}% paid</span>
            </div>
            <div style="background:rgba(255,255,255,.06);border-radius:10px;height:6px;overflow:hidden;margin-bottom:6px;">
              <div :style="{width: paidPct(order) + '%', background:'#50C878', height:'100%', transition:'width .3s'}"></div>
            </div>
            <div style="font-size:12px;margin-bottom:8px;">
              Paid <strong style="color:#50C878;">KES {{ formatPrice(order.paid_amount || 0) }}</strong>
              of <strong>{{ (order.currency || 'KES') }} {{ formatPrice(order.total_amount) }}</strong>.
              Outstanding: <strong>KES {{ formatPrice(Math.max(0, order.total_amount - (order.paid_amount || 0))) }}</strong>
            </div>
            <button class="btn btn-sm btn-warning fw-bold" @click.stop="sendNextSplit(order)" :disabled="continuingPayment === order.id">
              <span v-if="continuingPayment === order.id"><span class="spinner-border spinner-border-sm me-1"></span>Sending…</span>
              <span v-else><i class="mdi mdi-cellphone-arrow-down me-1"></i>Send next M-Pesa prompt</span>
            </button>
          </div>

          <!-- Same partial card for sellers, info-only -->
          <div v-if="order.status === 'partially_paid' && role === 'seller'" class="ord-hint info">
            <i class="mdi mdi-call-split me-1"></i>Buyer has paid <strong>KES {{ formatPrice(order.paid_amount || 0) }}</strong> of <strong>{{ (order.currency || 'KES') }} {{ formatPrice(order.total_amount) }}</strong> ({{ paidPct(order) }}%). Awaiting remainder.
          </div>

          <!-- Buyer cancellation: pre-dispatch only (paid / confirmed / preparing) -->
          <div v-if="role==='buyer' && canCancel(order.status)" class="d-flex justify-content-end mt-2">
            <button class="btn btn-sm btn-outline-danger" @click.stop="openCancelModal(order)">
              <i class="mdi mdi-close-circle-outline me-1"></i>Request Cancellation
            </button>
          </div>

          <!-- Seller cancellation: allowed up to in_transit (animal sick/died) -->
          <div v-if="role==='seller' && canSellerCancel(order.status)" class="d-flex justify-content-end mt-2">
            <button class="btn btn-sm btn-outline-danger" @click.stop="openCancelModal(order)">
              <i class="mdi mdi-close-circle-outline me-1"></i>Cancel &amp; Refund Buyer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Cancellation request modal -->
    <div v-if="cancelOrder" class="ord-overlay" @click.self="closeCancelModal">
      <div class="ord-modal" :style="themeCardStyle">
        <div class="ord-modal-head">
          <h6 class="m-0"><i class="mdi mdi-close-circle-outline me-1" style="color:#FF6B6B;"></i>Cancel order #{{ cancelOrder.order_number }}</h6>
          <button class="btn-close-x" @click="closeCancelModal">&times;</button>
        </div>
        <div class="ord-modal-body">
          <div class="ord-card-meta mb-2">{{ cancelOrder.listing_title || 'Order' }} · {{ (cancelOrder.currency || 'KES') }} {{ formatPrice(cancelOrder.total_amount) }}</div>
          <div style="background:rgba(255,107,107,.08);border:1px solid rgba(255,107,107,.25);border-radius:8px;padding:8px 12px;font-size:11px;color:#FF9E9E;margin-bottom:12px;">
            <i class="mdi mdi-information-outline me-1"></i>
            <span v-if="role==='seller'">The buyer will be automatically refunded the full amount to their wallet, and your listing will be paused.</span>
            <span v-else>Cancellations are auto-approved before dispatch. Your payment will be refunded to your wallet immediately.</span>
          </div>
          <label class="fm-label">Reason</label>
          <select v-if="role==='seller'" v-model="cancelReason" class="fm-input w-100 mb-2" :style="themeInputStyle">
            <option value="">— select a reason —</option>
            <option>Animal fell sick</option>
            <option>Animal died</option>
            <option>Animal no longer available</option>
            <option>Cannot fulfil delivery</option>
            <option>Other</option>
          </select>
          <select v-else v-model="cancelReason" class="fm-input w-100 mb-2" :style="themeInputStyle">
            <option value="">— select a reason —</option>
            <option>Changed my mind</option>
            <option>Wrong item</option>
            <option>Found cheaper elsewhere</option>
            <option>Seller unresponsive</option>
            <option>Delivery delay</option>
            <option>Other</option>
          </select>
          <label class="fm-label">Notes (optional)</label>
          <textarea v-model="cancelNotes" class="fm-input w-100" rows="3" :style="themeInputStyle" :placeholder="role==='seller' ? 'Anything the buyer should know' : 'Anything the seller should know'"></textarea>
        </div>
        <div class="ord-modal-foot d-flex gap-2">
          <button class="btn btn-sm btn-outline-secondary flex-fill" @click="closeCancelModal" :disabled="cancelling">Keep order</button>
          <button class="btn btn-sm btn-danger flex-fill" @click="submitCancellation" :disabled="!cancelReason || cancelling">
            <span v-if="cancelling"><span class="spinner-border spinner-border-sm me-1"></span>Cancelling...</span>
            <span v-else><i class="mdi mdi-check me-1"></i>Confirm cancellation</span>
          </button>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>
<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>

<style>
:root{
  --fm-h:#F5F0E8;
  --fm-s:#6B6356;
  --fm-m:#A09888;
  --fm-bg1:#141414;
  --fm-bg2:#0F0F0F;
  --fm-bg3:#1E1E1E;
  --fm-br:#1E1E1E;
  --fm-page:#0A0A0A;
}

html[data-theme="light"]{
  --fm-h:#1A1A1A;
  --fm-s:#777777;
  --fm-m:#555555;
  --fm-bg1:#FFFFFF;
  --fm-bg2:#F7F5F2;
  --fm-bg3:#F0EBE4;
  --fm-br:#E8E2DA;
  --fm-page:#F7F5F2;
}

html[data-theme="light"] body,
html[data-theme="light"] #layout-wrapper,
html[data-theme="light"] .orders-root,
html[data-theme="light"] .orders-container{
  background:var(--fm-page)!important;
  color:var(--fm-h)!important;
}

html[data-theme="dark"] body,
html[data-theme="dark"] #layout-wrapper,
html[data-theme="dark"] .orders-root,
html[data-theme="dark"] .orders-container{
  background:var(--fm-page)!important;
  color:var(--fm-h)!important;
}

html[data-theme="light"] .ord-topbar{
  background:#FFFFFF!important;
  border-bottom-color:#E8E2DA!important;
}

html[data-theme="dark"] .ord-topbar{
  background:var(--fm-bg1,#141414)!important;
}

html[data-theme="light"] .ord-toggle,
html[data-theme="light"] .ord-status-chip,
html[data-theme="light"] .ord-card,
html[data-theme="light"] .ord-empty-card{
  background:#FFFFFF!important;
  border-color:#E8E2DA!important;
  color:#1A1A1A!important;
}

html[data-theme="light"] .ord-card-title,
html[data-theme="light"] .ord-price,
html[data-theme="light"] .ord-section-title{
  color:#1A1A1A!important;
}

html[data-theme="light"] .ord-meta,
html[data-theme="light"] .ord-submeta,
html[data-theme="light"] .ord-empty-subtext{
  color:#555!important;
}

html[data-theme="light"] .ord-toggle.active{
  background:rgba(191,161,100,.12)!important;
  color:#BFA164!important;
}

html[data-theme="light"] .ord-status-chip.active{
  background:rgba(191,161,100,.12)!important;
  color:#BFA164!important;
}
</style>

<style scoped>
.orders-root{background:#0A0A0A;min-height:100vh;color:var(--fm-h,#F5F0E8)}
.ord-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:rgba(10,10,10,0.95);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.ord-container{padding-top:80px;padding-bottom:60px;max-width:800px}
.ord-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);display:flex;align-items:center;justify-content:center;z-index:9999;padding:16px}
.ord-modal{width:100%;max-width:440px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;overflow:hidden}
.ord-modal-head{padding:14px 16px;border-bottom:1px solid var(--fm-br,#1E1E1E);display:flex;justify-content:space-between;align-items:center}
.ord-modal-body{padding:14px 16px}
.ord-modal-foot{padding:12px 16px;border-top:1px solid var(--fm-br,#1E1E1E)}
.btn-close-x{background:none;border:none;color:var(--fm-m,#A09888);font-size:22px;line-height:1;cursor:pointer}
.fm-label{display:block;font-size:11px;font-weight:600;color:var(--fm-m,#A09888);margin-bottom:4px;text-transform:uppercase;letter-spacing:.3px}
.fm-input{padding:8px 10px;border-radius:8px;font-size:13px}
.ord-role-toggle{display:flex;border-radius:10px;overflow:hidden;border:1px solid var(--fm-br,#2A2520)}
.ord-role-toggle button{flex:1;padding:10px;background:var(--fm-bg1,#141414);border:none;color:var(--fm-m,#A09888);font-size:13px;font-weight:600;cursor:pointer;transition:all .2s}
.ord-role-toggle button.active{background:rgba(191,161,100,0.15);color:#BFA164}
.ord-tabs{display:flex;gap:6px;overflow-x:auto;scrollbar-width:none}
.ord-tabs::-webkit-scrollbar{display:none}
.ord-tabs button{white-space:nowrap;padding:6px 14px;border-radius:16px;border:1px solid var(--fm-br,#2A2520);background:var(--fm-bg1,#141414);color:var(--fm-m,#A09888);font-size:12px;cursor:pointer;transition:all .2s}
.ord-tabs button.active{background:rgba(191,161,100,0.15);border-color:#BFA164;color:#BFA164}
.ord-tab-badge{background:#BFA164;color:#0A0A0A;font-size:10px;font-weight:700;padding:1px 6px;border-radius:10px;margin-left:4px}
.ord-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:16px;margin-bottom:12px;cursor:pointer;transition:all .2s}
.ord-card:hover{border-color:#BFA164;transform:translateY(-1px)}
.ord-card-img{width:56px;height:56px;border-radius:8px;overflow:hidden;background:var(--fm-bg2,#0F0F0F);flex-shrink:0;display:flex;align-items:center;justify-content:center}
.ord-card-img img{width:100%;height:100%;object-fit:cover}
.ord-order-num{font-size:11px;color:var(--fm-s,#6B6356);font-weight:600;font-family:monospace}
.ord-card-title{font-size:14px;font-weight:600;color:var(--fm-h,#F5F0E8)}
.ord-card-meta{font-size:11px;color:var(--fm-s,#6B6356);margin-top:2px}
.ord-card-price{font-size:16px;font-weight:700;color:#BFA164}
.ord-status-badge{font-size:10px;font-weight:600;padding:2px 8px;border-radius:10px;text-transform:capitalize}
.st-pending_payment{background:rgba(255,171,0,0.15);color:#FFAB00}
.st-paid{background:rgba(80,200,120,0.15);color:#50C878}
.st-confirmed{background:rgba(80,165,241,0.15);color:#50a5f1}
.st-shipped{background:rgba(155,89,182,0.15);color:#9b59b6}
.st-delivered{background:rgba(80,200,120,0.15);color:#50C878}
.st-completed{background:rgba(80,200,120,0.2);color:#50C878}
.st-cancelled{background:rgba(255,107,107,0.15);color:#FF6B6B}
.st-payment_initiated{background:rgba(255,171,0,0.15);color:#FFAB00}
.st-stk_sent{background:rgba(255,171,0,0.15);color:#FFAB00}
.st-in_transit{background:rgba(155,89,182,0.15);color:#9b59b6}
.st-pending_seller_confirmation{background:rgba(191,161,100,0.15);color:#BFA164}
.st-inspection{background:rgba(80,165,241,0.15);color:#50a5f1}
.st-disputed{background:rgba(255,107,107,0.15);color:#FF6B6B}
.st-failed{background:rgba(255,107,107,0.15);color:#FF6B6B}
.ord-hint{font-size:11px;padding:8px 12px;border-radius:8px;margin-top:10px}
.ord-hint.warn{background:rgba(255,171,0,0.08);color:#FFAB00}
.ord-hint.ok{background:rgba(80,200,120,0.08);color:#50C878}
.ord-hint.info{background:rgba(80,165,241,0.08);color:#50a5f1}
/* amber/green/blue on pale tints are too dim in light mode */
html[data-theme="light"] .st-payment_initiated,
html[data-theme="light"] .st-stk_sent,
html[data-theme="light"] .ord-hint.warn{color:#B76E00}
html[data-theme="light"] .ord-hint.ok,
html[data-theme="light"] .st-delivered,
html[data-theme="light"] .st-completed{color:#1F7A46}
html[data-theme="light"] .ord-hint.info,
html[data-theme="light"] .st-inspection{color:#1D6FB8}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .ord-container { padding-top: 70px; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)); padding-left: 12px; padding-right: 12px; }
  .ord-card { padding: 12px; border-radius: 10px; }
  .ord-modal-head, .ord-modal-foot { padding: 10px 12px; }
  .ord-overlay { padding: 12px; align-items: flex-end; }   /* bottom-sheet style on mobile */
  .ord-overlay > div { max-height: 85vh; overflow-y: auto; border-radius: 14px 14px 0 0; }
}
</style>
<script>
/* eslint-disable */
import {  execute, executeJson, flocker, uploads  } from "@/api";

function applyOrdersTheme(vm) {
  const t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || (vm && vm.darkMode ? 'dark' : 'light');
  const isDark = t !== 'light';
  if (vm) vm.darkMode = isDark;
  window.__flockrSetTheme(isDark ? 'dark' : 'light');

  if (document.body) {
    document.body.setAttribute('data-sidebar', isDark ? 'dark' : 'light');
    document.body.style.background = isDark ? '#0A0A0A' : '#F7F5F2';
    document.body.style.color = isDark ? '#F5F0E8' : '#1A1A1A';
  }

  const wrapper = document.getElementById('layout-wrapper');
  if (wrapper) {
    wrapper.style.background = isDark ? '#0A0A0A' : '#F7F5F2';
    wrapper.style.color = isDark ? '#F5F0E8' : '#1A1A1A';
  }
}

export default {
  name: "OrdersPage",
  data: function() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
      themeObserver:null,
      loading: true, role: 'buyer', activeTab: 'all', orders: [],
      cancelOrder: null, cancelReason: '', cancelNotes: '', cancelling: false,
      continuingPayment: null,
      statusTabs: [
        { label: 'All', value: 'all' },{ label: 'Pending', value: 'pending_payment' },
        { label: 'Initiated', value: 'payment_initiated' },
        { label: 'Partial', value: 'partially_paid' },
        { label: 'Paid', value: 'paid' },{ label: 'Confirmed', value: 'confirmed' },
        { label: 'Pending Handover', value: 'pending_seller_confirmation' },
        { label: 'In Transit', value: 'in_transit' },
        { label: 'Delivered', value: 'delivered' },{ label: 'Inspection', value: 'inspection' },
        { label: 'Completed', value: 'completed' },
        { label: 'Disputed', value: 'disputed' },
        { label: 'Cancelled', value: 'cancelled' }
      ]
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    filteredOrders() {
      if (this.activeTab === 'all') return this.orders;
      var tab = this.activeTab;
      return this.orders.filter(function(o) { return o.status === tab; });
    }
  },
  mounted: function() {
    applyOrdersTheme(this);
    this.themeObserver = new MutationObserver(() => applyOrdersTheme(this));
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    if (this.$route.query.role === 'seller') this.role = 'seller';
    this.fetchOrders();
  },
  beforeUnmount: function() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }
  },
  methods: {
    photoUrl(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},
    formatPrice(v){if(!v)return'0';return Number(v).toLocaleString()},
    formatDate(d){if(!d)return'';return new Date(d).toLocaleDateString('en-KE',{day:'numeric',month:'short',year:'numeric'})},
    formatStatus(s){return(s||'').replace(/_/g,' ')},
    canCancel(status){ return ['paid','confirmed','preparing'].indexOf(status) !== -1; },
    canSellerCancel(status){ return ['paid','confirmed','preparing','dispatched','pending_seller_confirmation','in_transit'].indexOf(status) !== -1; },
    paidPct(order){
      var total = Number(order.total_amount || 0);
      var paid  = Number(order.paid_amount || 0);
      return total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
    },
    async sendNextSplit(order){
      if (this.continuingPayment) return;
      var outstanding = Math.max(0, Number(order.total_amount) - Number(order.paid_amount || 0));
      if (!confirm("Send the next M-Pesa prompt for KES " + Number(outstanding).toLocaleString() + " outstanding on order " + order.order_number + "? (Your phone will receive a prompt for up to KES 150,000)")) return;
      this.continuingPayment = order.id;
      try {
        var f = new FormData();
        f.append('function', 'payNextSplit');
        // Prefer order_group_id if the order is part of a cart; otherwise use the order_id
        if (order.order_group_id) f.append('orderGroupId', order.order_group_id);
        else                      f.append('orderId', order.id);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          alert('M-Pesa prompt sent to your phone. Enter your PIN to complete payment ' + r.data.data.splitSeq + ' of ' + r.data.data.splitTotal + ' (KES ' + Number(r.data.data.amountThisChunk).toLocaleString() + ').');
          // Refresh the list after a short delay so the new paid_amount shows up once the buyer pays
          var self = this;
          setTimeout(function(){ self.fetchOrders(); }, 8000);
        } else {
          alert((r.data && r.data.message) || 'Could not send next prompt');
        }
      } catch(e) { alert('Network error — try again'); }
      this.continuingPayment = null;
    },
    openCancelModal(order){ this.cancelOrder = order; this.cancelReason = ''; this.cancelNotes = ''; },
    closeCancelModal(){ if(this.cancelling) return; this.cancelOrder = null; },
    async submitCancellation(){
      if(!this.cancelOrder || !this.cancelReason) return;
      var userId = sessionStorage.getItem('userId');
      if(!userId) { this.$router.push('/login'); return; }
      var isSeller = this.role === 'seller';
      var confirmMsg = isSeller
        ? 'Cancel order #'+this.cancelOrder.order_number+'? The buyer will be refunded KES '+this.formatPrice(this.cancelOrder.total_amount)+' automatically and your listing will be paused.'
        : 'Cancel order #'+this.cancelOrder.order_number+' and refund KES '+this.formatPrice(this.cancelOrder.total_amount)+' to your wallet?';
      if(!confirm(confirmMsg)) return;
      this.cancelling = true;
      try{
        var f = new FormData();
        f.append('function', isSeller ? 'sellerCancelOrder' : 'requestOrderCancellation');
        f.append('orderId', this.cancelOrder.id);
        f.append(isSeller ? 'sellerId' : 'userId', userId);
        f.append('reason', this.cancelReason);
        f.append('notes', this.cancelNotes||'');
        var r = await execute(f, flocker);
        if(r.data && r.data.success){
          alert(r.data.message || 'Order cancelled');
          this.cancelOrder = null;
          await this.fetchOrders();
        } else {
          alert((r.data && r.data.message) || 'Cancellation failed');
        }
      }catch(e){ alert('Network error — try again'); }
      this.cancelling = false;
    },
    getCount(st){if(st==='all')return this.orders.length;return this.orders.filter(function(o){return o.status===st}).length},
    async fetchOrders(){
      this.loading=true;
      var userId=sessionStorage.getItem('userId');
      if(!userId){this.$router.push('/login');return}
      try{
        var f=new FormData();
        f.append('function','getMyOrders');
        f.append('userId',userId);
        f.append('role',this.role);
        var r=await execute(f,flocker);
        if(r.data&&r.data.success) {
          this.orders=r.data.data.orders||[];
          // Auto-switch: if no orders on current tab, check the other
          if(this.orders.length===0 && !this._autoSwitched){
            this._autoSwitched=true;
            var otherRole = this.role === 'buyer' ? 'seller' : 'buyer';
            var f2=new FormData();
            f2.append('function','getMyOrders');
            f2.append('userId',userId);
            f2.append('role',otherRole);
            var r2=await execute(f2,flocker);
            if(r2.data&&r2.data.success&&(r2.data.data.orders||[]).length>0){
              this.role=otherRole;
              this.orders=r2.data.data.orders;
            }
          }
        }
      }catch(e){console.error(e)}
      finally{this.loading=false}
    }
  }
};
</script>
